import { Activity, ArrowLeft, Brain, Heart, Pill, Stethoscope } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { usePageMeta } from '../../lib/seo.tsx';

const symptoms = [
  { icon: Stethoscope, text: "Can't find pulse (of the page)" },
  { icon: Heart, text: 'Irregular heartbeat (404 error)' },
  { icon: Brain, text: 'Confusion about location' },
  { icon: Pill, text: 'Need a dose of correct URL' },
];

export default function NotFound() {
  const [showDiagnosis, setShowDiagnosis] = useState(false);
  const navigate = useNavigate();

  usePageMeta({
    title: 'ITGL Academy | Page Not Found',
    description: 'The page you are looking for could not be found.',
    pathname: '/404',
    noIndex: true,
  });

  return (
    <div className="text-foreground flex h-full min-h-screen flex-col items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <h1 className="text-primary mb-4 text-4xl font-bold md:text-6xl">404</h1>
        <h2 className="mb-8 text-2xl font-semibold md:text-3xl">
          Page Not Found
        </h2>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="bg-card mb-8 max-w-lg rounded-lg p-6 shadow-lg"
      >
        <h3 className="mb-4 text-xl font-semibold">Patient Symptoms:</h3>
        <ul className="space-y-2">
          {symptoms.map((symptom, index) => (
            <motion.li
              key={symptom.text}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="flex items-center space-x-2"
            >
              <symptom.icon className="text-primary h-5 w-5 shrink-0" />
              <span>{symptom.text}</span>
            </motion.li>
          ))}
        </ul>
      </motion.div>

      {!showDiagnosis && (
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.3 }}
          onClick={() => setShowDiagnosis(true)}
          className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center space-x-2 rounded-md px-4 py-2 transition-colors"
        >
          <Activity className="h-4 w-4" />
          <span>Show Diagnosis</span>
        </motion.button>
      )}

      <AnimatePresence>
        {showDiagnosis && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="mt-4 max-w-md text-center"
          >
            <h3 className="mb-4 text-xl font-semibold">Diagnosis:</h3>
            <p className="mb-4">
              The patient is suffering from Acute URL Deficiency Syndrome
              (AUDS). This condition occurs when the requested web page is not
              found on the server.
            </p>
            <h3 className="mb-4 text-xl font-semibold">Treatment Plan:</h3>
            <p className="mb-4">
              The recommended treatment is to navigate back to a known healthy
              page. This will restore proper website functionality and alleviate
              symptoms of confusion.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/')}
              className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center space-x-2 rounded-md px-4 py-2 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Return to Homepage</span>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
